import { z } from "zod";

/**
 * Shared DTO cho Admin Reconcile board (US-AE5.F1-0018 / @UC9040 reconcile-dashboard · @UC9045
 * reconcile-rerun & export). Một nguồn sự thật cho api (S01 — engine + redaction theo vai) và
 * admin web (S03 — render bảng + re-run + export), tránh drift shape ở 2 nơi (DRY).
 *
 * BẢO MẬT / BẤT BIẾN (invariants):
 * - B1: tiền = BigInt minor units serialize thành STRING trên wire (không float/number JS mất chính
 *   xác). Field money redact-được (balanceCacheMinor/ledgerSumMinor/driftMinor/totalDriftMinor) =
 *   `string | null` — vai không được xem tiền (SUPPORT/READ_ONLY) → null (redaction theo vai F1).
 * - `.strict()` — KHÔNG khai báo field lạ; field lạ bị reject tại biên (chống vô tình leak).
 * - Cursor opaque string — đối xứng common/pagination/cursor.
 * - G2: DTO chỉ mô tả kết quả ĐỌC (reconcile so cache vs Σledger); KHÔNG có field mutating ledger.
 *
 * Envelope khớp quy ước list repo: `{ data, pageInfo: { hasNext, nextCursor } }`.
 */

/** Max page-size cho bảng reconcile. Default (20) do backend áp khi vắng. */
export const ADMIN_RECONCILE_MAX_LIMIT = 100;

/** Trạng thái đối soát một account: pass (cache == Σledger) | drift (lệch). */
export const reconcileStatusSchema = z.enum(["pass", "drift"]);
export type ReconcileStatus = z.infer<typeof reconcileStatusSchema>;

/**
 * Money string trên wire = BigInt minor units. Regex chỉ cho số nguyên (dấu âm hợp lệ vì
 * driftMinor luôn ≥0 nhưng ledgerSum/cache có thể âm). Reject float (AC3 S02) & rác. `null` khi
 * redact theo vai (SUPPORT/READ_ONLY).
 */
const moneyMinorString = z
  .string()
  .regex(/^-?\d+$/, "money phải là số nguyên minor units (không float)");
const moneyMinorNullable = moneyMinorString.nullable();

/**
 * Query GET /admin/reconcile — filter status/fundId/periodDate + cursor pagination + format=csv.
 * limit ∈ [1,100] optional; cursor opaque (≤512). periodDate ISO date (YYYY-MM-DD) optional.
 */
export const reconcileDashboardQuerySchema = z.object({
  status: reconcileStatusSchema.optional(),
  fundId: z.string().uuid().optional(),
  periodDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "periodDate phải là YYYY-MM-DD")
    .optional(),
  cursor: z.string().max(512).optional(),
  limit: z.coerce.number().int().min(1).max(ADMIN_RECONCILE_MAX_LIMIT).optional(),
  format: z.enum(["csv"]).optional(),
});

export type ReconcileDashboardQuery = z.infer<typeof reconcileDashboardQuerySchema>;

/**
 * Một dòng đối soát per-account (UC9040 AC1). Money = STRING minor | NULL (redact theo vai).
 * `driftMinor` = |balanceCacheMinor − ledgerSumMinor| (tính bằng BigInt phía server, không float).
 */
export const reconcileStatusRowSchema = z
  .object({
    accountId: z.string(),
    fundId: z.string(),
    fundName: z.string().nullable(),
    accountKind: z.string(),
    currency: z.string(),
    /** BigInt minor units as string; null khi redact vai thấp. KHÔNG number. */
    balanceCacheMinor: moneyMinorNullable,
    ledgerSumMinor: moneyMinorNullable,
    driftMinor: moneyMinorNullable,
    status: reconcileStatusSchema,
    checkedAt: z.string(),
  })
  .strict();

export type ReconcileStatusRow = z.infer<typeof reconcileStatusRowSchema>;

/** Tổng hợp trên đầu bảng (UC9040 AC1). totalDriftMinor = STRING minor | NULL (redact). */
export const reconcileSummarySchema = z
  .object({
    totalAccounts: z.number().int().min(0),
    driftCount: z.number().int().min(0),
    /** Σ|drift| minor units as string; null khi redact vai thấp. */
    totalDriftMinor: moneyMinorNullable,
    /** Σdebit == Σcredit toàn hệ (double-entry). false ⇒ báo động. */
    doubleEntryBalanced: z.boolean(),
  })
  .strict();

export type ReconcileSummary = z.infer<typeof reconcileSummarySchema>;

/** Envelope bảng reconcile `{ data, pageInfo, summary }`. */
export const reconcileDashboardResponseSchema = z
  .object({
    data: z.array(reconcileStatusRowSchema),
    pageInfo: z.object({
      hasNext: z.boolean(),
      nextCursor: z.string().nullable(),
    }),
    summary: reconcileSummarySchema,
  })
  .strict();

export type ReconcileDashboardResponse = z.infer<typeof reconcileDashboardResponseSchema>;

/**
 * Body POST /admin/reconcile/run (UC9045 AC2). periodDate optional (kỳ đối soát; default hôm nay).
 * KHÔNG tham số mutating ledger — re-run chỉ recompute cache (idempotent). `.strict()`.
 */
export const reconcileRunRequestSchema = z
  .object({
    periodDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, "periodDate phải là YYYY-MM-DD")
      .optional(),
  })
  .strict();

export type ReconcileRunRequest = z.infer<typeof reconcileRunRequestSchema>;

/** Response POST /admin/reconcile/run (UC9045 AC2). totalDriftMinor = STRING minor. */
export const reconcileRunResponseSchema = z
  .object({
    runId: z.string(),
    status: reconcileStatusSchema,
    accountsChecked: z.number().int().min(0),
    driftCount: z.number().int().min(0),
    /** Σ|drift| minor units as string (không redact ở đây — chỉ OPS/SUPER chạy được). */
    totalDriftMinor: moneyMinorString,
    doubleEntryBalanced: z.boolean(),
    ranAt: z.string(),
  })
  .strict();

export type ReconcileRunResponse = z.infer<typeof reconcileRunResponseSchema>;
